import { KatalComponent, Keys } from '../../shared/base';

/**
 * @component {kat-selection-list} KatalSelectionList Control to allow users to choose between a vertical list of items
 * @example CategoryPage {"label": "Category page", "items":[ { "name":"name", "value":"1" }, { "name":"name", "value":"2" }, { "name":"name", "value":"3" } ]}
 * @event selected Selected event will fire when one of the categories have been selected
 * @jsonFormat items [ { "name":"name", "value":"1" }, { "name":"name", "value":"2" }, { "name":"name", "value":"3" } ]
 * @status Production
 */
export class KatalSelectionList extends KatalComponent {
  static get observedAttributes() {
    return ['label', 'items', 'selected'];
  }

  constructor() {
    super(KatalSelectionList.observedAttributes);
    this._items = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @classprop {string} label This is the label of the list
   * @required
   */
  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @classprop {string} selected This is the reflected selected list item
   */
  get selected() {
    return this.getAttribute('selected') || '';
  }

  set selected(value) {
    this.setAttribute('selected', value);
  }

  /**
   * @classprop {json} items An array of list items to be displayed
   * @required
   */
  get items() {
    return this._items;
  }

  set items(value) {
    if (Array.isArray(value)) {
      this._items = value;
    } else if (typeof value === 'string') {
      try {
        this.items = value === '' ? [] : JSON.parse(value);
        // Avoid the double render
        return;
      } catch (err) {
        console.error('Items must be set as a valid stringified JSON - ' + err);
      }
    } else {
      console.error('Items must be set as an Array');
    }

    this.render();
  }

  render() {
    if (!this.propertiesHaveBeenUpgraded) {
      return;
    }

    this.innerHTML = '';
    const selectionListLabel = document.createElement('div');
    selectionListLabel.classList.add('selection-list-label');
    selectionListLabel.setAttribute('tabIndex', '0');

    const chevron = document.createElement('span');
    chevron.classList.add('chevron');

    const label = document.createElement('span');
    label.textContent = this.label;

    const katList = document.createElement('kat-list');
    katList.variant = 'unordered';

    selectionListLabel.appendChild(chevron);
    selectionListLabel.appendChild(label);

    this.buildItemList(this.items, katList);

    this.appendChild(selectionListLabel);
    this.appendChild(katList);

    this.clickHandler();
  }

  clickHandler() {
    this.querySelector('.selection-list-label').addEventListener('click', e => {
      e.stopImmediatePropagation();

      const event = new CustomEvent('click', {
        bubbles: true,
        detail: { click: this },
      });
      this.dispatchEvent(event);
    });

    const clickHandler = e => {
      // If we get a keypress but it's not a space (32) or enter key (13), disregard
      if (
        e.type === 'keydown' &&
        ![Keys.Space, Keys.Enter].includes(e.keyCode)
      ) {
        return;
      }

      e.stopImmediatePropagation();

      if (e.target.tagName === 'LI') {
        const element = e.target;
        const parent = element.parentElement;
        const parentListItems = parent.querySelectorAll('li');

        if (this.selected !== '') {
          const previousSelected = Array.prototype.filter.call(
            parentListItems,
            el => el.dataset.value === this.selected
          );
          previousSelected[0].classList.remove('selected');
        }

        this.selected = element.dataset.value;
        element.classList.add('selected');

        this.triggerSelectedEvent(element.dataset.value);
      }
    };

    const innerList = this.querySelector('kat-list');
    innerList.addEventListener('keydown', clickHandler);
    innerList.addEventListener('click', clickHandler);
  }

  buildItemList(items, parent) {
    items.forEach(item => {
      if (item.children) {
        const childSelectionList = document.createElement('kat-selection-list');
        childSelectionList.label = item.name;
        childSelectionList.items = item.children;

        parent.appendChild(childSelectionList);
      } else {
        const listItem = document.createElement('li');
        listItem.dataset.value = item.value;
        listItem.innerText = item.name;
        if (item.value === this.selected) {
          listItem.classList.add('selected');
        }

        parent.appendChild(listItem);
      }
    });
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    if (attr === 'items') {
      this.items = newValue;
    } else if (newValue !== this.selected) {
      if (attr === 'selected') {
        this.triggerSelectedEvent(newValue);
      }
      this.render();
    }
  }

  triggerSelectedEvent(value) {
    this.dispatchCustomEvent('selected', { selected: value });
  }
}

KatalComponent.register('kat-selection-list', KatalSelectionList);
